import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import Documents from './pages/Documents';
import WellbeingCentredCare from './pages/WellbeingCentredCare';
import ServantLeadership from './pages/ServantLeadership';
import CQCRegistration from './pages/CQCRegistration';
import ContactUs from './pages/ContactUs';
import CareSuites from './pages/CareSuites';
import Activities from './pages/Activities';
import MeetTheTeam from './pages/MeetTheTeam';
import PhotoGallery from './pages/PhotoGallery';
import RespiteCare from './pages/RespiteCare';
import DementiaCare from './pages/DementiaCare';
import NursingCare from './pages/NursingCare';
import QualityAssurance from './pages/QualityAssurance';
import GMBUnion from './pages/GMBUnion';
import Governance from './pages/Governance';
import DigitalTransformation from './pages/DigitalTransformation';
import SelfFunding from './pages/SelfFunding';
import SocialServicesFunding from './pages/SocialServicesFunding';
import NHSFunding from './pages/NHSFunding';
import WorkWithUs from './pages/WorkWithUs';
import HelpAndAdvice from './pages/HelpAndAdvice';
import FundingCareHomeFees from './pages/FundingCareHomeFees';
import GuideChoosingCareHome from './pages/GuideChoosingCareHome';
import UnderstandingDifferentStagesDementia from './pages/UnderstandingDifferentStagesDementia';
import IsItTimeForCareHome from './pages/IsItTimeForCareHome';
import WhatIsPersonCentredCare from './pages/WhatIsPersonCentredCare';
import HowPalliativeCareFundedUk from './pages/HowPalliativeCareFundedUk';
import WhatToPackMovingCareHomeChecklist from './pages/WhatToPackMovingCareHomeChecklist';
import GuideMovingHospitalCareHome from './pages/GuideMovingHospitalCareHome';
import IntroducingProactiveRehabCentre from './pages/IntroducingProactiveRehabCentre';
import Testimonials from './pages/Testimonials';
import OurHomes from './pages/OurHomes';
import Developments from './pages/Developments';
import BroomfieldHouse from './pages/developments/BroomfieldHouse';
import SouthlandsHouse from './pages/developments/SouthlandsHouse';
import DevelopmentProgressTimeline from './pages/DevelopmentProgressTimeline';
import Rehabilitation from './pages/Rehabilitation';
import RMOnline from './pages/RMOnline';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import { isArchived } from './config/archivedPages';
import { Helmet } from 'react-helmet-async';

function Home() {
  // Array of image paths for the slideshow
  const heroImages = [
    '/Rodwell-Front.jpg',
    '/Rodwell-back.jpg'
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('fade-in');

  useEffect(() => {
    // Only run slideshow if there are multiple images
    if (heroImages.length <= 1) return;

    const interval = setInterval(() => {
      setFadeClass('fade-out');
      
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
        setFadeClass('fade-in');
      }, 500); // Half of transition duration (1s = 1000ms, half = 500ms)
    }, 10000); // Change image every 10 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  useEffect(() => {
    const scriptSrc = 'https://player.vimeo.com/api/player.js';
    if (document.querySelector(`script[src="${scriptSrc}"]`)) return;

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <div className="page">
      <Helmet>
        <title>Graham Care | Nursing &amp; Dementia Care Homes</title>
        <meta
          name="description"
          content="Graham Care operates nursing-led care homes with spacious care suites, dementia support, and funding options for families and communities."
        />
      </Helmet>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />

      <main id="main-content">
        <section className="hero">
          <div 
            className={`hero__image ${fadeClass}`} 
            style={{ backgroundImage: `url('${heroImages[currentImageIndex]}')` }}
          ></div>
          <div className="hero__content">
            <h1 className="hero__title">
              <span>Welcome to</span>
              <span className="hero__title-main">Graham Care</span>
            </h1>
            <div className="hero__contact-icons">
              <a href="tel:01932832900" className="hero__contact-icon" aria-label="Call us">
                <span className="icon-phone">📞</span>
              </a>
              <a href="mailto:enquiries@rodwell.house" className="hero__contact-icon" aria-label="Email us">
                <span className="icon-email">✉️</span>
              </a>
            </div>
            <Link to="/our-homes" className="btn-enquiry">Explore Our Homes</Link>
          </div>
        </section>

        <section className="content-section introduction">
          <div className="section__container">
            <div className="section__text">
              <h2>Providing Homes for Life</h2>
              <h3>Who we are</h3>
              <p>We provide care and support to those with complex care needs, with capacity for a 1,000 residents across Surrey and Kent, in purpose-built facilities and care suites.</p>
              <p>Our story began in 1996, when Ernie and Dr Karen Graham founded their first care home with the mission to provide exceptional care and homes for life for its residents. We are still a family-run business with the same mission today in all of our five services.</p>
            </div>
          </div>
          <div className="introduction__video">
            <div className="introduction__video-inner">
              <iframe
                src="https://player.vimeo.com/video/927078896?badge=0&autopause=0&player_id=0&app_id=58479"
                title="Ernie Graham - Graham Care"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section className="content-section care-suites">
          <div className="section__container">
            <div className="section__text">
              <h2>Care Suites</h2>
              <p>Our homes consist of our larger-than-average Care Suites that come equipped with en-suite bathrooms, kitchenettes and living and dining areas. They are purpose-built with residents and their families in mind, each room encouraging independence as well as visits from loved ones.</p>
              <p>Learn more about our care suites <Link to="/care-suites" className="nurse-care-link">here</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/care-suites-image.jpg" alt="Care Suites" />
            </div>
          </div>
        </section>

        <section className="content-section quality-facilities">
          <div className="section__container">
            <div className="section__text">
              <h2>Purpose-Built, Quality Facilities</h2>
              <p>Built with residents at the heart of their design, our homes offer high quality, care-orientated facilities, creating the ideal environment for people to feel safe and at home. Many of our locations sit in peaceful grounds with strong links to local health services and community amenities.</p>
              <p>To view our upcoming developments, click <Link to="/developments" className="nurse-care-link">here</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/Rodwell-back.jpg" alt="Graham Care home — purpose-built facilities and grounds" />
            </div>
          </div>
        </section>

        <section className="content-section serving-community">
          <div className="section__container">
            <div className="section__text">
              <h2>Serving the Community</h2>
              <p>Across our homes, residents reflect the communities we serve. We help families access Local Authority or NHS funding where private funding may not be an option, making high quality care as accessible as possible.</p>
              <p>
                We help families understand funding options for care.{' '}
                {!isArchived('/contact-us') && (
                  <>
                    <Link to="/contact-us" className="nurse-care-link">Contact us</Link> to discuss what may apply in your circumstances.
                  </>
                )}
              </p>
            </div>
            <div className="section__image">
              <img src="/Rodwell-Front.jpg" alt="Graham Care home serving the local community" />
            </div>
          </div>
        </section>

        <section className="content-section care-services">
          <div className="section__container">
            <div className="section__text">
              <h2>Long-Term and Short-Term Care Services</h2>
              <p>Our homes can be a &apos;home for life&apos; for those who require long-term care, whilst also offering short-term stays for rehabilitation, assessment, or respite before returning home.</p>
              <p>
                Read more about our{' '}
                <Link to="/respite-care" className="nurse-care-link">respite care</Link>,{' '}
                <Link to="/nursing-care" className="nurse-care-link">nursing care</Link> and{' '}
                <Link to="/rehabilitation" className="nurse-care-link">rehabilitation</Link>.
              </p>
            </div>
            <div className="section__image">
              <img src="/grahamcare-127.jpg" alt="Long-Term and Short-Term Care Services" />
            </div>
          </div>
        </section>

        <section className="content-section nurse-led-care">
          <div className="section__container">
            <div className="section__text">
              <h2>24/7 Nurse-Led Care</h2>
              <p>Our experienced and trained nurses are always on-hand to support our residents. They lead and oversee each resident's care at every step of their care journeys, from initial assessment, to reviews of the care plan, to their personalised social programme.</p>
              <p>Read more about our approach to <Link to="/nursing-care" className="nurse-care-link">nursing care</Link> and <Link to="/dementia-care" className="nurse-care-link">dementia care</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/iStock-1165981632-scaled.jpg" alt="24/7 Nurse-Led Care" />
            </div>
          </div>
        </section>

        <section className="content-section relationship-care">
          <div className="section__container">
            <div className="section__text">
              <h2>Wellbeing Centred Care</h2>
              <p>We believe that a person's wellbeing is more than the sum of their physical needs — it is about living meaningfully, comfortably, and with dignity, even in the context of frailty or cognitive decline.</p>
              <p>Read more about Wellbeing Centred Care <Link to="/wellbeing-centred-care" className="nurse-care-link">here</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/pillars-of-wellbeing.jpg" alt="Wellbeing Centred Care" />
            </div>
          </div>
        </section>

      </main>

      <footer className="footer">
        <section className="footer__enquiry">
          <div className="footer__enquiry-container">
            <Link to="/contact-us" className="footer__enquiry-link">
              Enquire about Graham Care <span className="footer__arrow">▶</span>
            </Link>
          </div>
        </section>
        <div className="footer__container">
          <section className="contact-section">
            <div className="contact-section__content">
              <p>
                For enquiries about our homes, please{' '}
                <Link to="/contact-us" className="nurse-care-link">
                  contact us
                </Link>
                .
              </p>
              <p>Copyright © Graham Care Ltd</p>
            </div>
          </section>
        </div>
      </footer>
    </div>
  );
}

function NotFound() {
  return (
    <div className="page">
      <Header />
      <main id="main-content" style={{ padding: '4rem 1.5rem', textAlign: 'center' }}>
        <h1>Page not found</h1>
        <p>This page is not available. It may have been moved or is not yet published.</p>
        <Link to="/">Return to home</Link>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/our-homes" element={<OurHomes />} />
        <Route path="/developments" element={<Developments />} />
        <Route path="/developments/broomfield" element={<BroomfieldHouse />} />
        <Route path="/developments/southlands" element={<SouthlandsHouse />} />
        <Route path="/developments/progress/:slug" element={<DevelopmentProgressTimeline />} />
        <Route path="/rmonline" element={<RMOnline />} />
        {!isArchived('/documents') && <Route path="/documents" element={<Documents />} />}
        {!isArchived('/wellbeing-centred-care') && <Route path="/wellbeing-centred-care" element={<WellbeingCentredCare />} />}
        {!isArchived('/testimonials') && <Route path="/testimonials" element={<Testimonials />} />}
        {!isArchived('/servant-leadership') && <Route path="/servant-leadership" element={<ServantLeadership />} />}
        {!isArchived('/cqc-registration') && <Route path="/cqc-registration" element={<CQCRegistration />} />}
        {!isArchived('/contact-us') && <Route path="/contact-us" element={<ContactUs />} />}
        {!isArchived('/care-suites') && <Route path="/care-suites" element={<CareSuites />} />}
        {!isArchived('/activities') && <Route path="/activities" element={<Activities />} />}
        {!isArchived('/meet-the-team') && <Route path="/meet-the-team" element={<MeetTheTeam />} />}
        {!isArchived('/photo-gallery') && <Route path="/photo-gallery" element={<PhotoGallery />} />}
        {!isArchived('/respite-care') && <Route path="/respite-care" element={<RespiteCare />} />}
        {!isArchived('/dementia-care') && <Route path="/dementia-care" element={<DementiaCare />} />}
        {!isArchived('/nursing-care') && <Route path="/nursing-care" element={<NursingCare />} />}
        <Route path="/rehabilitation" element={<Rehabilitation />} />
        {!isArchived('/rehab') && <Route path="/rehab" element={<Rehabilitation />} />}
        {!isArchived('/quality-assurance') && <Route path="/quality-assurance" element={<QualityAssurance />} />}
        {!isArchived('/governance') && <Route path="/governance" element={<Governance />} />}
        {!isArchived('/digital-transformation') && (
          <Route path="/digital-transformation" element={<DigitalTransformation />} />
        )}
        {!isArchived('/gmb-union') && <Route path="/gmb-union" element={<GMBUnion />} />}
        {!isArchived('/self-funding') && <Route path="/self-funding" element={<SelfFunding />} />}
        {!isArchived('/social-services-funding') && <Route path="/social-services-funding" element={<SocialServicesFunding />} />}
        {!isArchived('/nhs-funding') && <Route path="/nhs-funding" element={<NHSFunding />} />}
        {!isArchived('/work-with-us') && <Route path="/work-with-us" element={<WorkWithUs />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/funding-care-home-fees-whitstable" element={<FundingCareHomeFees />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/guide-choosing-care-home-whitstable" element={<GuideChoosingCareHome />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/understanding-different-stages-dementia" element={<UnderstandingDifferentStagesDementia />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/is-it-time-for-care-home" element={<IsItTimeForCareHome />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/what-is-person-centred-care" element={<WhatIsPersonCentredCare />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/how-palliative-care-funded-uk" element={<HowPalliativeCareFundedUk />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/what-to-pack-moving-care-home-checklist" element={<WhatToPackMovingCareHomeChecklist />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/guide-moving-hospital-care-home" element={<GuideMovingHospitalCareHome />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/introducing-whitstable-house-proactive-rehab-centre" element={<IntroducingProactiveRehabCentre />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice" element={<HelpAndAdvice />} />}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
